import { useEffect } from 'react'
import { apiPost } from '../api/client'

/**
 * `ProactiviteeLiveList` din proactivitee.js: la 2 secunde, cifrele VII
 * (CPU / RAM / swap / conexiuni) pentru randurile de pe pagina, prin
 * `POST /proactivitee/live-batch` (Proactivitee::getLivePerformanceBatch).
 * Serverul trece agentul pe monitorizare la 1 s pe fiecare masina raspunsa
 * (DeviceWatch::touchMany), cat timp lista e deschisa.
 *
 * Randurile se retuseaza pe loc (`[data-od-live]`), nu prin starea tabelului:
 * o redesenare la doua secunde ar inchide randul desfasurat si ar pierde
 * selectia.
 */

export interface LiveRow {
  has: boolean
  cpu_util: number
  ram_used_percent: number
  ram_swap_percent: number
  net_connections: number
  age_seconds: number | null
}

export const LIST_EVERY = 2000

const R = 5
const C = 2 * Math.PI * R

/** Discul plin, ca easyPieChart(size 20, lineWidth 10) - acelasi desen ca la Inventoree. */
export function gaugeHtml(percent: unknown, kind: string): string {
  const p = Math.max(0, Math.min(100, Number(percent) || 0))
  const dash = (p / 100) * C
  return (
    `<span class="easychart mini mini-chart priority" data-od-live="${kind}" data-bar-color="#F00" data-percent="${p}"` +
    ' style="display:inline-block;position:relative;width:20px;height:20px;vertical-align:middle">' +
    '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" style="transform:rotate(-90deg)">' +
    `<circle cx="10" cy="10" r="${R}" fill="none" stroke="#DDD" stroke-width="10"></circle>` +
    `<circle class="od-live-arc" cx="10" cy="10" r="${R}" fill="none" stroke="#F00" stroke-width="10" stroke-dasharray="${dash.toFixed(3)} ${C.toFixed(3)}"></circle>` +
    '</svg>' +
    `<span class="percent_partitions" style="position:absolute;left:0;top:0;width:20px;line-height:20px;text-align:center;font-size:7px;color:#fff">${Math.round(p)}%</span>` +
    '</span>'
  )
}

function setGauge(el: Element | null, percent: number): void {
  if (!el) return
  const p = Math.max(0, Math.min(100, Number(percent) || 0))
  el.setAttribute('data-percent', String(p))
  el.querySelector('.od-live-arc')?.setAttribute('stroke-dasharray', `${((p / 100) * C).toFixed(3)} ${C.toFixed(3)}`)
  const label = el.querySelector('.percent_partitions')
  if (label) label.textContent = `${Math.round(p)}%`
}

export function applyLive(table: Element, map: Record<string, LiveRow>): void {
  for (const [id, data] of Object.entries(map)) {
    if (!data?.has) continue
    const tr = table.querySelector(`#row_${id}`)
    if (!tr) continue
    setGauge(tr.querySelector('[data-od-live="cpu"]'), data.cpu_util)
    setGauge(tr.querySelector('[data-od-live="ram"]'), data.ram_used_percent)
    setGauge(tr.querySelector('[data-od-live="swap"]'), data.ram_swap_percent)
    const net = tr.querySelector('[data-od-live="net"]')
    if (net) net.textContent = String(parseInt(String(data.net_connections), 10) || 0)
  }
}

export function fetchLive(ids: number[]): Promise<Record<string, LiveRow>> {
  return apiPost<Record<string, LiveRow>>('/proactivitee/live-batch', { devices: ids })
}

/** Porneste bataia cat timp tabelul e pe ecran; se opreste singura cand pagina pleaca. */
export function useLiveBatch(tableId: string, ids: number[], enabled: boolean): void {
  const key = ids.join(',')
  useEffect(() => {
    if (!enabled || key === '') return
    let live = true
    const tick = async () => {
      const table = document.getElementById(tableId)
      if (!table || !live) return
      try {
        const map = await fetchLive(key.split(',').map(Number))
        if (live) applyLive(table, map)
      } catch {
        /* o bataie pierduta nu opreste ceasul */
      }
    }
    void tick()
    const timer = window.setInterval(() => void tick(), LIST_EVERY)
    return () => {
      live = false
      window.clearInterval(timer)
    }
  }, [tableId, key, enabled])
}
